import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

type ApiKeyScope =
  | 'notifications:send'
  | 'notifications:read'
  | 'live:create'
  | 'live:update'
  | 'live:read'
  | 'users:write'
  | 'users:read'
  | 'devices:write'
  | 'devices:read'

interface ApiKey {
  id: string
  tenant_id: string
  application_id: string | null
  name: string
  key_prefix: string
  scopes: ApiKeyScope[]
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

export function useApiKeys() {
  return useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => api.get<ApiKey[]>('/dashboard/api-keys'),
  })
}

interface CreateApiKeyData {
  name: string
  application_id?: string
  scopes: ApiKeyScope[]
  expires_in_days?: number
}

interface CreateApiKeyResponse extends ApiKey {
  key: string // Only returned on creation
}

export function useCreateApiKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateApiKeyData) => {
      return api.post<CreateApiKeyResponse>('/dashboard/api-keys', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
    },
  })
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/dashboard/api-keys/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
    },
  })
}

export type { ApiKeyScope }
